import { isEqual } from 'lodash'

import { TSearchType } from 'src/shared/types'

import { GetDoctors } from 'src/api/types'
import { formatResults, getSearchType } from './helpers'
import { defaultState } from './initialState'
import { State, Action, ActionTypes } from './types'

export const reducer = (state: State, action: Action): State => {
  const { type, payload } = action

  switch (type) {
    case ActionTypes.SEARCH_PROVIDERS: {
      const {
        search = state.search,
        filter = state.filter,
        cancelTokenSource,
      } = payload

      const hasFilterChanged = !isEqual(filter, defaultState.filter)
      const isFirstSearch = false

      const newState = {
        ...state,
        search,
        filter,
        hasFilterChanged,
        isFirstSearch,
        itemType: getSearchType(search, state.category),
        isLoading: true,
        cancelTokenSource,
        errorMsg: undefined,
        isRequestCanceled: false,
      }

      return isEqual(newState, state) ? state : newState
    }
    case ActionTypes.SEARCH_PROVIDERS_FULFILLED: {
      const { data, updatedFilter, countItems, countProviders, maxTotalCount } =
        payload

      const isNameSearch = !!(
        state.search?.name && state.search.name.length > 0
      )

      const displayItems = formatResults(
        data.data,
        state.itemType as TSearchType,
        isNameSearch
      )

      const getDoctorsCount = (data as GetDoctors).count

      const totalCount = isNameSearch
        ? displayItems?.length
        : getDoctorsCount.total

      const isVirtualCareAvailable =
        (getDoctorsCount?.virtualOption?.y ?? 0) > 0 ||
        (getDoctorsCount?.virtualOption?.only ?? 0) > 0

      const isAcceptingNewPatients =
        (getDoctorsCount?.isAcceptingNewPatients ?? 0) > 0

      let maxTotalCounts = {}
      if (maxTotalCount) {
        maxTotalCounts = { maxTotalCount }
      } else if (!maxTotalCount && isNameSearch) {
        maxTotalCounts = countItems as any
      } else {
        maxTotalCounts = countProviders as any
      }

      const newState: State = {
        ...state,
        results: data.data,
        isLoading: false,
        displayItems,
        totalCount,
        isVirtualCareAvailable,
        isAcceptingNewPatients,
        unfilteredItems: displayItems,
        specialtyFilters: data.specialtyFilters,
        filter: updatedFilter,
        expandedPractice: defaultState.expandedPractice,
        viewPractitionerList: [],
        ...maxTotalCounts,
      }

      return isEqual(newState, state) ? state : newState
    }
    case ActionTypes.SEARCH_RADIUS_INCREASE: {
      const { distance, errorMsg, previousDistance } = payload

      return {
        ...state,
        isLoading: true,
        filter: {
          ...state.filter,
          distance,
        },
        errorMsg,
        previousDistance,
      }
    }

    case ActionTypes.SEARCH_PROVIDERS_FAILED:
      console.error(payload)

      return {
        ...state,
        totalCount: 0,
        results: [],
        displayItems: [],
        unfilteredItems: [],

        isLoading: false,
      }

    case ActionTypes.GET_PRACTITIONERS: {
      const list = state.viewPractitionerList
      const existingObject = list.find(el => el.practice === payload)

      if (!existingObject) {
        list.push({
          practice: payload,
        })
      }

      return {
        ...state,
        isPractitionerListLoading: true,
        loadingPractice: payload,
        viewPractitionerList: list,
      }
    }
    case ActionTypes.GET_PROVIDERS_BY_PRACTICE_ID_SUCCEED: {
      const list = state.viewPractitionerList
      const practiceIndex = list.findIndex(
        el => el.practice === state.loadingPractice
      )

      list[practiceIndex].list = payload

      return {
        ...state,
        isPractitionerListLoading: false,
        loadingPractice: undefined,
        viewPractitionerList: list,
      }
    }
    case ActionTypes.GET_PROVIDERS_BY_PRACTICE_ID_FAILED:
      console.error(payload)

      return {
        ...state,
        isPractitionerListLoading: false,
        loadingPractice: undefined,
        viewPractitionerList: [],
      }

    case ActionTypes.ON_REQUEST_FILTER:
      return {
        ...state,
        filter: payload,
      }

    case ActionTypes.ON_FILTER:
      return {
        ...state,
        displayItems: payload,
        totalCount: payload.length,
      }

    case ActionTypes.CANCEL_REQUEST:
      return {
        ...state,
        filter: {
          ...state.filter,
          distance: 20,
        },
        cancelTokenSource: undefined,
        isRequestCanceled: true,
      }

    case ActionTypes.TOGGLE_LIST_VIEW:
      return {
        ...state,
        listView: state.listView === 'list' ? 'map' : 'list',
      }

    case ActionTypes.SET_EXPANDED_PRACTICE:
      return {
        ...state,
        expandedPractice: payload.practiceId,
      }

    default:
      return state
  }
}
